import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  fetchPluginConfig,
  updatePluginConfig,
} from '@sportninja/common/services/account'
import type {
  PluginConfig,
  PluginConfigPayload,
} from '@sportninja/common/features/account/apiAccess'

/** The account's Website Plugin settings. Waits for the account to load. */
export const usePluginConfig = (accountUid: string | undefined) =>
  useQuery<PluginConfig>({
    queryKey: queryKeys.accountApiAccess.pluginConfig(accountUid),
    queryFn: () => fetchPluginConfig(accountUid as string),
    enabled: Boolean(accountUid),
  })

/**
 * Saves the settings and puts the saved copy in the cache. `retry: 0`, as with
 * the key mutations: a save is never retried behind the user's back.
 */
export const useSavePluginConfig = (accountUid: string) => {
  const queryClient = useQueryClient()

  return useMutation<PluginConfig, Error, PluginConfigPayload>({
    mutationFn: (payload) => updatePluginConfig(accountUid, payload),
    onSuccess: (config) =>
      queryClient.setQueryData(
        queryKeys.accountApiAccess.pluginConfig(accountUid),
        config
      ),
    retry: 0,
  })
}
